import { supabase } from '@/config/supabase'
import { fetchZoneQuotazioni, fetchCoefficientiExtra } from '@/services/zone-quotazioni.service'
import { fetchCorrezioni, getCorrezione } from '@/services/omi-correzioni.service'
import type { ValuationFormData, ValuationResult, AppliedCoefficient } from '@/types'

// Determina il prezzo base in base allo stato dell'immobile
function getPrezzoBase(
  prezzoAbitabile: number,
  prezzoRistrutturato: number,
  prezzoDaRistrutturare: number,
  condition?: string
): number {
  const pickByCondition = (): number => {
    switch (condition) {
      case 'nuovo':
      case 'ristrutturato':
        return prezzoRistrutturato
      case 'da_ristrutturare':
        return prezzoDaRistrutturare
      case 'buono':
      default:
        return prezzoAbitabile
    }
  }

  const chosen = pickByCondition()
  // Fallback a cascata se valore mancante/NaN/<=0
  if (Number.isFinite(chosen) && chosen > 0) return chosen
  if (Number.isFinite(prezzoAbitabile) && prezzoAbitabile > 0) return prezzoAbitabile
  if (Number.isFinite(prezzoRistrutturato) && prezzoRistrutturato > 0) return prezzoRistrutturato
  if (Number.isFinite(prezzoDaRistrutturare) && prezzoDaRistrutturare > 0) return prezzoDaRistrutturare
  throw new Error('Prezzi zona non validi: tutti i valori sono 0 o non numerici')
}

// Calcola valutazione immobiliare
export async function calculateValuation(
  formData: ValuationFormData
): Promise<ValuationResult> {
  if (!formData.zoneName) {
    throw new Error('Zona non selezionata')
  }

  // 1. Carica dati zone, coefficienti e correzioni OMI da Supabase
  const [zones, coefficientiExtra, correzioni] = await Promise.all([
    fetchZoneQuotazioni(),
    fetchCoefficientiExtra(),
    fetchCorrezioni(),
  ])

  const zona = zones.find((z) => z.nome === formData.zoneName)
  if (!zona) {
    throw new Error(`Zona "${formData.zoneName}" non trovata`)
  }

  // 2. Determina prezzo base da stato immobile
  const prezzoBase = getPrezzoBase(
    zona.prezzoAbitabile,
    zona.prezzoRistrutturato,
    zona.prezzoDaRistrutturare,
    formData.condition
  )

  // 3. Applica correzioni OMI (additive: globale + provincia + caratteristiche + servizi)
  const appliedCoefficients: AppliedCoefficient[] = []
  let omiCorrection = 0

  // Helper per push additivo + tracking
  const applyAdditive = (key: string, type: string, label: string) => {
    const v = getCorrezione(correzioni, key)
    if (v !== 0) {
      omiCorrection += v
      appliedCoefficients.push({
        type,
        key,
        value: v,
        description: `${label} ${v > 0 ? '+' : ''}${Math.round(v * 1000) / 10}%`,
      })
    }
  }

  // Globale: aggiornamento mercato
  applyAdditive('aggiornamento_mercato', 'omi_globale', 'Aggiornamento mercato')

  // Provincia
  applyAdditive('corr_provincia_CA', 'omi_provincia', 'Correzione provincia Cagliari')

  // Caratteristiche derivate da campi esistenti
  if (formData.floor === 'quarto' || formData.floor === 'quinto_o_superiore') {
    applyAdditive('piano_alto_bonus', 'omi_caratteristiche', 'Piano alto')
  }
  if (formData.floor === 'terra' || formData.floor === 'interrato') {
    applyAdditive('piano_terra_malus', 'omi_caratteristiche', 'Piano terra / interrato')
  }
  if ((formData.bathrooms ?? 0) >= 2) {
    applyAdditive('doppi_servizi', 'omi_caratteristiche', 'Doppi servizi')
  }

  // Caratteristiche da nuovi campi form
  if (formData.energyClass === 'alta') {
    applyAdditive('classe_energetica_alta', 'omi_caratteristiche', 'Classe energetica A/B')
  } else if (formData.energyClass === 'bassa') {
    applyAdditive('classe_energetica_bassa', 'omi_caratteristiche', 'Classe energetica F/G')
  } else if (formData.energyClass === 'media') {
    appliedCoefficients.push({
      type: 'omi_caratteristiche',
      key: 'classe_energetica_media',
      value: 0,
      description: 'Classe energetica C/D/E (standard, nessuna correzione)',
    })
  }
  if (formData.isRecente) {
    applyAdditive('recente_costruzione', 'omi_caratteristiche', 'Costruzione recente (ultimi 10 anni)')
  }

  // Servizi extra (correzioni OMI categoria servizi)
  if (formData.hasTerrazzoGrande) {
    applyAdditive('terrazzo_grande', 'omi_servizi', 'Terrazzo ampio (>20mq)')
  }
  if (formData.hasGiardinoPrivato) {
    applyAdditive('giardino_privato', 'omi_servizi', 'Giardino privato (>50mq)')
  }
  if (formData.hasPostoBarca) {
    applyAdditive('posto_barca', 'omi_servizi', 'Posto barca')
  }
  if (formData.hasDomotica) {
    applyAdditive('domotica', 'omi_servizi', 'Impianto domotica')
  }

  // Tipo immobile speciale: MOLTIPLICATIVO (non additivo). Default 1.
  let omiTipoMultiplier = 1
  if (formData.specialType === 'attico') {
    const m = getCorrezione(correzioni, 'molt_attico')
    if (m > 0) {
      omiTipoMultiplier = m
      appliedCoefficients.push({
        type: 'omi_tipo_immobile', key: 'molt_attico', value: m - 1,
        description: `Attico / Ultimo piano ×${m}`,
      })
    }
  } else if (formData.specialType === 'loft') {
    const m = getCorrezione(correzioni, 'molt_loft')
    if (m > 0) {
      omiTipoMultiplier = m
      appliedCoefficients.push({
        type: 'omi_tipo_immobile', key: 'molt_loft', value: m - 1,
        description: `Loft / Open space ×${m}`,
      })
    }
  } else if (formData.specialType === 'monolocale') {
    const m = getCorrezione(correzioni, 'molt_monolocale')
    if (m > 0) {
      omiTipoMultiplier = m
      appliedCoefficients.push({
        type: 'omi_tipo_immobile', key: 'molt_monolocale', value: m - 1,
        description: `Monolocale / Bilocale piccolo ×${m}`,
      })
    }
  }

  // Prezzo base corretto con fattori OMI additivi + moltiplicatore tipo speciale
  const prezzoBaseCorretto = prezzoBase * (1 + omiCorrection) * omiTipoMultiplier

  // 4. Calcola coefficienti additivi caratteristiche
  let totalMultiplier = 1

  // Posizione pregiata (zona-specifico)
  if (formData.view && formData.view !== 'nessuna') {
    const bonus = formData.view === 'parziale'
      ? zona.posizioneBonus / 2
      : zona.posizioneBonus
    totalMultiplier += bonus
    appliedCoefficients.push({
      type: 'posizione',
      key: formData.view,
      value: bonus,
      description: formData.view === 'parziale' ? 'Posizione buona' : 'Posizione pregiata / panoramica',
    })
  }

  // Senza ascensore (zona-specifico, solo se no ascensore)
  if (!formData.hasElevator) {
    totalMultiplier += zona.senzaAscensore
    appliedCoefficients.push({
      type: 'ascensore',
      key: 'false',
      value: zona.senzaAscensore,
      description: 'Senza ascensore',
    })
  }

  // Pertinenza (zona-specifico)
  if (formData.hasPertinenza) {
    totalMultiplier += zona.pertinenzaBonus
    appliedCoefficients.push({
      type: 'pertinenza',
      key: 'true',
      value: zona.pertinenzaBonus,
      description: 'Con pertinenza (cantina, soffitta)',
    })
  }

  // Esposizione ottima
  if (formData.hasGoodExposure) {
    totalMultiplier += zona.esposizioneBonus
    appliedCoefficients.push({
      type: 'esposizione',
      key: 'true',
      value: zona.esposizioneBonus,
      description: 'Esposizione ottima',
    })
  }

  // Spazi esterni
  if (formData.hasExternalSpaces) {
    totalMultiplier += zona.spaziEsterniBonus
    appliedCoefficients.push({
      type: 'spazi_esterni',
      key: 'true',
      value: zona.spaziEsterniBonus,
      description: 'Spazi esterni (terrazzo, balcone, giardino)',
    })
  }

  // Garage/Box
  if (formData.hasGarage) {
    const garageBonus = formData.garageType === 'posto_auto'
      ? coefficientiExtra.postoAuto
      : coefficientiExtra.box
    totalMultiplier += garageBonus
    appliedCoefficients.push({
      type: 'box',
      key: formData.garageType || 'box',
      value: garageBonus,
      description: formData.garageType === 'posto_auto' ? 'Posto auto' : 'Box auto',
    })
  }

  // Piscina
  if (formData.hasPool) {
    totalMultiplier += coefficientiExtra.piscina
    appliedCoefficients.push({
      type: 'piscina',
      key: 'true',
      value: coefficientiExtra.piscina,
      description: 'Piscina',
    })
  }

  // Residence
  if (formData.isResidence) {
    totalMultiplier += coefficientiExtra.residence
    appliedCoefficients.push({
      type: 'residence',
      key: 'true',
      value: coefficientiExtra.residence,
      description: 'In residence / Condominio con servizi',
    })
  }

  // 4. Moltiplicatore tipo immobile (moltiplicativo, non additivo)
  let tipoMultiplier = 1
  if (formData.propertyType === 'villa') {
    tipoMultiplier = zona.villa
    appliedCoefficients.push({
      type: 'tipo_immobile',
      key: 'villa',
      value: zona.villa - 1,
      description: 'Tipo: Villa',
    })
  } else if (formData.propertyType === 'villetta') {
    tipoMultiplier = zona.casa
    appliedCoefficients.push({
      type: 'tipo_immobile',
      key: 'villetta',
      value: zona.casa - 1,
      description: 'Tipo: Casa / Villetta',
    })
  }

  // 6. Calcola prezzo finale (base corretto OMI × caratteristiche × tipo)
  const pricePerSqm = prezzoBaseCorretto * totalMultiplier * tipoMultiplier
  const estimatedValue = pricePerSqm * formData.area

  // Range ±10%
  const minValue = estimatedValue * 0.90
  const maxValue = estimatedValue * 1.10

  return {
    estimatedValue: Math.round(estimatedValue),
    minValue: Math.round(minValue),
    maxValue: Math.round(maxValue),
    pricePerSqm: Math.round(pricePerSqm),
    zoneName: formData.zoneName,
    prezzoBaseMq: prezzoBase,
    appliedCoefficients,
    formData,
  }
}

// Salva valutazione su Supabase
export async function saveValuation(result: ValuationResult): Promise<string> {
  const { data, error } = await supabase
    .from('valuations_cagliari')
    .insert({
      address: [result.formData.address, result.formData.streetNumber]
        .map((v) => (v ?? '').trim())
        .filter(Boolean)
        .join(' ') || result.zoneName || 'Non specificato',
      latitude: result.formData.latitude,
      longitude: result.formData.longitude,
      zone_name_csv: result.zoneName,
      property_type: result.formData.propertyType,
      area: result.formData.area,
      rooms: result.formData.rooms,
      bathrooms: result.formData.bathrooms,
      floor: result.formData.floor,
      has_elevator: result.formData.hasElevator,
      condition: result.formData.condition,
      view: result.formData.view,
      has_pertinenza: result.formData.hasPertinenza,
      has_good_exposure: result.formData.hasGoodExposure,
      has_external_spaces: result.formData.hasExternalSpaces,
      has_garage: result.formData.hasGarage,
      garage_type: result.formData.garageType,
      has_pool: result.formData.hasPool,
      is_residence: result.formData.isResidence,
      energy_class: result.formData.energyClass ?? null,
      is_recente: result.formData.isRecente ?? false,
      special_type: result.formData.specialType ?? null,
      has_terrazzo_grande: result.formData.hasTerrazzoGrande ?? false,
      has_giardino_privato: result.formData.hasGiardinoPrivato ?? false,
      has_posto_barca: result.formData.hasPostoBarca ?? false,
      has_domotica: result.formData.hasDomotica ?? false,
      estimated_value: result.estimatedValue,
      min_value: result.minValue,
      max_value: result.maxValue,
      price_per_sqm: result.pricePerSqm,
      applied_coefficients: result.appliedCoefficients,
    })
    .select('id')
    .single()

  if (error) {
    console.error('Error saving valuation:', error)
    throw error
  }

  return data.id
}
